import { TreeDataNode } from "src/pages/ManageStudies/models/interfaces";
import { IDataConfig, IQueryMessage, IReferenceItem, StudyType } from "src/pages/NewAskKural/models/interfaces";
import { fileStatus, ICollaboratorData, UserAccessTypeString } from "src/pages/Studies/models/interfaces";

export enum ReportsTemplateType {
	Word = "word",
	PPT = "ppt",
}

export enum ReportPublishedStatusType {
	Draft = "draft",
	Published = "published",
}

export enum AutosaveStatusType {
	Saved = "saved",
	ValidationError = "validation_error",
	Error = "error",
	Saving = "saving",
	UnsavedChanges = "unsaved_changes",
	ToBeSaved = "to_be_saved",
}

export enum GenerationStatusType {
	Preview = "preview",
	Generating = "generating",
	Complete = "complete",
	Error = "error",
}

export enum ReportPreviewRenderedFromType {
	AddReport = "add-report",
	ViewReport = "view-report",
}

export interface IReportCollaborator {
	name: string;
	email: string;
	access_type: string;
}

export interface IReportPermissions {
	collaborators: ICollaboratorData[];
}

export interface IReport {
	report_gen_id: string;
	report_iteration_id: string;
	name: string;
	description?: string;
	file_type?: ReportsTemplateType;
	template?: {
		template_name?: string;
		template_id?: string;
	};
	status: ReportPublishedStatusType;
	updated_at: string;
	permissions: IReportPermissions;
	user_access_type: UserAccessTypeString;
}

export interface ICreateReportModalProps {
	isModalVisible: boolean;
	setIsModalVisible: (visible: boolean) => void;
	modalPosition?: { top?: number; left?: number };
}

export interface ICreateReportForm {
	name: string;
	description: string;
	template?: {
		template_name?: string;
		template_id?: string;
	};
	file_type: ReportsTemplateType;
}

// Define types for report-specific data structures
export interface IReportSection2 {
	section_id: string;
	order: number;
	name?: string;
	key_question?: string;
	instructions?: string;
	data_sources?: IDataSources;
	ai_summary?: {
		content: string | null;
		generated_at: string | null;
		last_edited_at: string | null;
		last_edited_by: {
			user_id: string;
			user_name: string;
		} | null;
	};
	subsections?: any[];
	processing_message?: string | null;
	display_on_ui?: boolean | null;
	redis_stream_key?: string[];
}

export interface IReportSection {
	section_id: string;
	name?: string;
	order?: number;
	data?: Record<string, any>;
	key_question?: string;
	instructions?: string;
	data_sources?: IDataSources;
	ai_summary?: {
		content?: string | null;
		generated_at?: string | null;
		last_edited_at?: string | null;
		last_edited_by?: {
			user_id?: string;
			user_name?: string;
		} | null;
	};
	updated_at?: string;
	is_edited?: boolean;
}

export interface GlobalInstruction {
	key_questions?: string;
	key_question?: string;
	additional_instructions?: string;
}

export interface IDataSources {
	entity_id?: string[];
	file_id?: string[];
	meta_id?: string[];
}

export interface IPendingUpdate {
	type: "section" | "guidelines";
	data: any;
	version: number;
}

interface ISaveResult {
	success: boolean;
	failedUpdates: string[];
}

export interface ICheckpoint {
	id: string;
	reportId: string;
	name: string;
	createdAt: string;
	createdBy: string;
}

export interface IIteration {
	id: string;
	reportId: string;
	version: number;
	changes: string;
	createdAt: string;
}

export interface CustomTreeNode extends TreeDataNode {
	id: string;
	type: "section" | "subsection" | "guidelines";
	parentId?: string;
	order: number;
	sectionData?: IReportSection;
}

export interface IReportPreviewCardProps {
	renderedFrom?: ReportPreviewRenderedFromType;
	sections: ISectionData[];
	setSections: (sections) => void;
	changesMap?: { [key: string]: boolean };
	setChangesMap?: (map: { [key: string]: boolean }) => void;
	globalSection: ISectionData;
	setGlobalSection: (section) => void;
	loading?: boolean;
	messages: IQueryMessage[];
	setMessages: (messages) => void;
	onOpenDataDrawer: (sectionId: string) => void;
	dataConfig: IDataConfig;
	isViewMode?: boolean;
	activeCitation?: { [key in StudyType]?: string | null };
	setActiveCitation?: (value) => void;
	handleReferenceDrawerOpen: () => void;
}

export interface IDatasetItem {
	_id: string;
	key: string;
	label: string;
	tags?: ITagItem[] | null;
}

export interface IStudyItem {
	_id: string;
	study_id: string;
	study: string;
	tags?: ITagItem[] | null;
	datasets: IDatasetItem[];
	category: string; // transcripts, reports, quant, etc.
}

export interface ITagItem {
	_id: string;
	value: string;
	label: string;
	category: string;
}

export interface ITagGroup {
	label: string;
	options: ITagItem[];
}

export interface TransformedReportData {
	studies: IStudyItem[];
	tagGroups: ITagGroup[];
	categories: string[];
}

export interface ISectionSelections {
	studies: string[];
	datasets: string[];
	tags: string[];
}

export interface IGeneralGuidelinesSectionProps {
	generalGuidelinesSection: any;
	onGuidelinesUpdate: (updatedGuidelines: any) => void;
	validationError?: {
		instructions?: string | null;
		[key: string]: any;
	};
	setValidationError?: (sectionId: string, field: string, error: string | null) => void;
	viewMode: boolean;
}

export interface ISectionHeaderProps {
	item: any;
	isEditing: boolean;
	editedName: string;
	nameError: string | null;
	isValidating: boolean;
	isSaving: boolean;
	isDeleting: boolean;
	isLocked: boolean;
	lockedBy: { name: string } | null;
	handleStartEdit: () => void;
	handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleNameBlur: () => void;
	handleNameKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	handleDelete: () => void;
	handleOpenFilterDrawer: () => void;
	viewMode: boolean;
}

export interface ISectionFieldsProps {
	keyQuestion: string;
	instructions: string;
	validationError: {
		key_question?: string | null;
		instructions?: string | null;
		name: string | null;
	};
	handleQuestionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	handleInstructionsChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	level: number;
	sectionId: string;
	isLocked: boolean;
	lockedBy: { name: string } | null;
	viewMode: boolean;
}

export interface ISectionComponentProps {
	item: any;
	level?: number;
	sectionId: string;
	handleSectionUpdate: (sectionId: string, updatedItem: any) => void;
	handleSectionDelete: (sectionId: string) => void;
	config?: typeof LEVEL_CONFIG;
	handleManualSave?: () => void;
	editingStates?: Record<string, any>;
	loadingStates?: Record<string, any>;
	onEdit?: (action: string, sectionId: string, payload?: any) => void;
	onOpenDataDrawer?: (sectionId: string) => void;
	handleSectionPreview?: () => void;
	validationError: {
		key_question?: string | null;
		instructions?: string | null;
		name?: string | null;
	};
	setValidationError: (sectionId: string, field: string, error: string | null) => void;
	removeValidationError: (sectionId: string, field: string) => void;
}

export interface ISectionFooterProps {
	showPreview: boolean;
	level: number;
	handleSectionPreview: () => void;
	isLocked: boolean;
	previewButtonDisabled: boolean;
	viewMode: boolean;
}

export const LEVEL_CONFIG = {
	0: {
		headerTag: "h3",
		className: "section-container",
		childrenKey: "sub_sections",
		childName: "Sub-section",
		showPreview: true,
		canAddChildren: true,
		itemType: "Section",
	},
	1: {
		headerTag: "h4",
		className: "sub-section",
		childrenKey: "sub_items",
		childName: "Sub-item",
		showPreview: false,
		canAddChildren: true,
		itemType: "Sub-section",
	},
	default: {
		headerTag: "h5",
		className: "nested-section",
		childrenKey: "children",
		childName: "Child",
		showPreview: false,
		canAddChildren: true,
		itemType: "Item",
	},
};
export interface IReportIssue {
	id: number;
	title: string;
	description: string;
	status: string;
	assignedTo?: string;
	createdDate?: string;
	permissions?: IReportPermissions;
}

export interface IReportIteration {
	_id: string;
	report_gen_id: string;
	name: string;
	description?: string;
	profile?: string;
	file_type: ReportsTemplateType;
	order: number;
	created_at: string;
	created_by: {
		user_id: string;
		user_name: string;
	};
	updated_at: string;
	updated_by: {
		user_id: string;
		user_name: string;
	};
	status: string; // "draft" | "published" etc.
	is_locked: boolean;
	locked_by: {
		user_id: string | null;
		user_name: string | null;
	};
	permissions: IReportPermissions;
	template: {
		template_id: string | null;
		template_name: string;
	};
	global_instruction: {
		key_question: string | null;
		additional_instructions: string | null;
	};
	global_data_sources: Record<string, any>;
	structure: {
		sections: any[];
	};
}

export interface ILeftPanelProps {
	iterations: IReportIteration[];
	loading?: boolean;
	onCompare?: (selectedIterations: IReportIteration[]) => void;
	onApply?: (selectedIterations: IReportIteration[]) => void;
	onNew?: () => void;
}

export interface IIssueIterationCardProps {
	iteration: IReportIteration;
	isSelected: boolean;
	onDelete: (reporGenId: string, reportIterationId: string) => void;
	onSelectionChange: (id: string, selected: boolean) => void;
	showCheckbox?: boolean;
}

export interface IReportCardProps {
	payload: IReport;
	handleDeleteReport: (id: string) => Promise<void>;
	activeReportId?: string;
	isSelected?: boolean;
	onSelect: () => void;
	onEditReport: () => void;
}

export interface ISectionData {
	section_id: string;
	order?: number;
	name: string;
	data: {
		instructions?: string;
		key_question?: string;
	};
	is_edited?: boolean;
	is_error?: boolean;
	unsavedChanges?: boolean;
	data_access_mismatch?: boolean;
	display_on_ui?: fileStatus | null;
	totalStudies?: number;
	totalTags?: number;
	totalDatasets?: number;
	key_question?: string;
	instructions?: string;
	data_sources?: IDataSources;
	ai_summary?: {
		content?: string | null;
		generated_at?: string | null;
		last_edited_at?: string | null;
		last_edited_by?: {
			user_id?: string;
			user_name?: string;
		} | null;
	};
	updated_at?: string;
}

export interface ISectionSummary {
	section_id: string;
	order: number;
	name: string;
	ai_summary?: {
		content: string | null;
		generated_at: string | null;
		last_edited_at: string | null;
		last_edited_by: {
			user_id: string;
			user_name: string;
		} | null;
	};
}

export interface ISectionLeftPanelProps {
	sections: ISectionData[];
	setSections: (sections) => void;
	changesMap: { [key: string]: boolean };
	setChangesMap: (map: { [key: string]: boolean }) => void;
	globalSection: any;
	setGlobalSection: (section: any) => void;
	dataConfig: IDataConfig;
	onSectionReorder?: (reorderedSections: ISectionData[]) => void;
}

export interface ISectionProps {
	item: ISectionData;
	sections?: ISectionData[];
	setSections: (sections) => void;
	changesMap: { [key: string]: boolean };
	setChangesMap?: (changes) => void;
	globalSection: any;
	setGlobalSection: (section: any) => void;
	viewMode: boolean;
	onOpenDataDrawer?: (sectionId: string) => void;
	handleDeleteSection: (section) => void;
}

export interface IReportDataDrawerProps {
	open: boolean;
	sectionId: string;
	sections: ISectionData[];
	setSections: (sections) => void;
	changesMap?: { [key: string]: boolean };
	setChangesMap?: (changes) => void;
	globalSection: ISectionData;
	setGlobalSection: (section) => void;
	dataConfig: IDataConfig;
	width?: number;
	loading?: boolean;
	isViewMode?: boolean;
}

export interface IFilterReportsProps {
	reportsData: IReport[];
	searchText: string;
	selectedCollaborators: { collaborator: string }[];
	selectedTag: string;
}

export interface IInitialReportData {
	is_locked: boolean;
	locked_by: {
		user_id: string;
		user_name: string;
	};
	global_instruction: {
		key_question: string | null;
		additional_instructions?: string;
		instructions?: string;
	};
	global_data_sources: {
		entity_id: string[];
		file_id: string[];
		meta_id: string[];
	};
	global_data_access_mismatch?: boolean;
	references?: {
		[key in StudyType]?: IReferenceItem[];
	};
	updated_at: string;
	structure: {
		sections: Array<{
			section_id: string;
			order: number;
			name: string;
			is_active: boolean;
			data_sources: {
				entity_id: string[];
				file_id: string[];
				meta_id: string[];
			};
			data_access_mismatch?: boolean;
			ai_summary: {
				content: string;
				generated_at: string;
				last_edited_at: string | null;
				last_edited_by: string | null;
			};
			subsections: any[];
			processing_message: string;
			display_on_ui: string;
			redis_stream_key: any[];
			is_edited: boolean;
			data: {
				key_question: string;
				instructions: string;
			};
		}>;
	};
	collaborators: Array<{
		collaborator_id: string;
		collaborator_name: string;
		user_access_type: string;
		is_owner: boolean;
	}>;
}
